import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Layout,
  Typography,
  Button,
  Menu,
  Avatar,
  Tooltip,
} from 'antd';
import {
  DashboardOutlined,
  CheckCircleOutlined,
  CarOutlined,
  UserOutlined,
  LogoutOutlined,
  CreditCardOutlined,
  StarOutlined,
  DollarOutlined,
  TransactionOutlined,
  LineChartOutlined,
  PlusSquareOutlined,
  ShopOutlined,
  MobileOutlined,
  FlagOutlined,
  BgColorsOutlined,
  LinkOutlined,
  FundOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import { useAdmin } from '../context/AdminContext';
import { AdminHeader } from './AdminHeader';
import { useLogout } from '../../hooks/useLogout';

const { Sider, Content } = Layout;
const { Title } = Typography;

interface AdminLayoutProps {
  children: React.ReactNode;
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export const ADMIN_TAB_META: Record<string, { labelKey: string; descKey: string }> = {
  overview:              { labelKey: 'admin.tab_overview',             descKey: 'admin.desc_overview' },
  users:                 { labelKey: 'admin.tab_users',                descKey: 'admin.desc_users' },
  'dealers':             { labelKey: 'Dealers',                         descKey: 'Manage all dealer accounts' },
  'dealer-packages':     { labelKey: 'admin.tab_dealer_packages',      descKey: 'admin.desc_dealer_packages' },
  'all-listings':        { labelKey: 'admin.tab_all_listings',         descKey: 'admin.desc_all_listings' },
  'bulk-upload':         { labelKey: 'Bulk Upload',                    descKey: 'Upload multiple listings at once for any dealer' },
  'single-listing':      { labelKey: 'Create Listing',                 descKey: 'Create a single listing on behalf of a dealer' },
  'credit-packages':     { labelKey: 'admin.tab_credit_packages',      descKey: 'admin.desc_credit_packages' },
  'promotion-packages':  { labelKey: 'admin.tab_promotion_packages',   descKey: 'admin.desc_promotion_packages' },
  payments:              { labelKey: 'admin.tab_payments',             descKey: 'admin.desc_payments' },
  'credit-transactions': { labelKey: 'admin.tab_credit_transactions',  descKey: 'admin.desc_credit_transactions' },
  'credit-analytics':    { labelKey: 'admin.tab_credit_analytics',     descKey: 'admin.desc_credit_analytics' },
  'vehicle-requests':    { labelKey: 'admin.tab_vehicle_requests',     descKey: 'admin.desc_vehicle_requests' },
  'vehicle-management':  { labelKey: 'admin.tab_vehicle_management',   descKey: 'admin.desc_vehicle_management' },
  'vehicle-features':    { labelKey: 'admin.tab_vehicle_features',     descKey: 'admin.desc_vehicle_features' },
  'push-notifications':  { labelKey: 'admin.tab_push_notifications',   descKey: 'admin.desc_push_notifications' },
  'reports':             { labelKey: 'admin.tab_reports',               descKey: 'admin.desc_reports' },
  'influencer-links':    { labelKey: 'admin.tab_influencer_links',      descKey: 'admin.desc_influencer_links' },
  'marketing-analytics': { labelKey: 'admin.tab_marketing_analytics',   descKey: 'admin.desc_marketing_analytics' },
  'giveaway':            { labelKey: 'admin.tab_giveaway',              descKey: 'admin.desc_giveaway' },
};

// Each section has a label (shown when expanded) and flat menu items.
// Using multiple flat Menus avoids antd's broken group+inlineCollapsed icon rendering.
const SIDEBAR_SECTIONS = [
  {
    sectionKey: 'admin.section_dashboard',
    items: [
      { key: 'overview', icon: <DashboardOutlined />, labelKey: 'admin.tab_overview' },
    ],
  },
  {
    sectionKey: 'admin.section_users_approvals',
    items: [
      { key: 'users',           icon: <UserOutlined />,  labelKey: 'admin.tab_users' },
      { key: 'dealers',         icon: <ShopOutlined />,  labelKey: 'Dealers' },
      { key: 'dealer-packages', icon: <ShopOutlined />,  labelKey: 'admin.tab_dealer_packages' },
    ],
  },
  {
    sectionKey: 'admin.section_listings',
    items: [
      { key: 'all-listings',       icon: <CarOutlined />,         labelKey: 'admin.tab_all_listings' },
      { key: 'bulk-upload',        icon: <PlusSquareOutlined />,  labelKey: 'Bulk Upload' },
      { key: 'single-listing',     icon: <PlusSquareOutlined />,  labelKey: 'Create Listing' },
      { key: 'vehicle-management', icon: <CarOutlined />,         labelKey: 'admin.tab_vehicle_management' },
      { key: 'vehicle-features',   icon: <BgColorsOutlined />,    labelKey: 'admin.tab_vehicle_features' },
      { key: 'vehicle-requests',   icon: <PlusSquareOutlined />,  labelKey: 'admin.tab_vehicle_requests' },
    ],
  },
  {
    sectionKey: 'admin.section_financials',
    items: [
      { key: 'credit-packages',     icon: <CreditCardOutlined />,  labelKey: 'admin.tab_credit_packages' },
      { key: 'promotion-packages',  icon: <StarOutlined />,        labelKey: 'admin.tab_promotion_packages' },
      { key: 'payments',            icon: <DollarOutlined />,      labelKey: 'admin.tab_payments' },
      { key: 'credit-transactions', icon: <TransactionOutlined />, labelKey: 'admin.tab_credit_transactions' },
    ],
  },
  {
    sectionKey: 'admin.section_analytics',
    items: [
      { key: 'credit-analytics', icon: <LineChartOutlined />, labelKey: 'admin.tab_credit_analytics' },
    ],
  },
  {
    sectionKey: 'admin.section_engagement',
    items: [
      { key: 'push-notifications', icon: <MobileOutlined />, labelKey: 'admin.tab_push_notifications' },
      { key: 'reports',            icon: <FlagOutlined />,    labelKey: 'admin.tab_reports' },
    ],
  },
  {
    sectionKey: 'admin.section_marketing',
    items: [
      { key: 'influencer-links',    icon: <LinkOutlined />, labelKey: 'admin.tab_influencer_links' },
      { key: 'marketing-analytics', icon: <FundOutlined />, labelKey: 'admin.tab_marketing_analytics' },
      { key: 'giveaway',            icon: <GiftOutlined />, labelKey: 'admin.tab_giveaway' },
    ],
  },
];

export const AdminLayout: React.FC<AdminLayoutProps> = ({
  children,
  activeTab,
  onTabChange,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const { user } = useAdmin();
  const { logout } = useLogout();
  const { t } = useTranslation();

  const metaKeys = ADMIN_TAB_META[activeTab] ?? { labelKey: 'admin.tab_overview', descKey: '' };
  const meta = { label: t(metaKeys.labelKey), description: metaKeys.descKey ? t(metaKeys.descKey) : '' };

  return (
    <Layout className="admin-layout-wrapper">
      {/* ── Sidebar ── */}
      <Sider
        width={252}
        collapsed={collapsed}
        collapsedWidth={64}
        theme="dark"
        trigger={null}
        className="admin-sider"
      >
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Brand / Logo */}
          <div
            style={{
              padding: collapsed ? '16px 0' : '16px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 10,
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              marginBottom: 4,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                background: 'linear-gradient(135deg, #FE5A1D 0%, #D24613 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 17,
                fontWeight: 800,
                flexShrink: 0,
                boxShadow: '0 3px 10px rgba(254, 90, 29, 0.4)',
              }}
            >
              A
            </div>
            {!collapsed && (
              <div style={{ minWidth: 0 }}>
                <Title
                  level={5}
                  style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: 14, lineHeight: '20px' }}
                >
                  AutoPlace
                </Title>
                <div
                  style={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: 'var(--admin-primary)',
                    textTransform: 'uppercase',
                    letterSpacing: '1.5px',
                    lineHeight: '13px',
                  }}
                >
                  {t('admin.admin_panel')}
                </div>
              </div>
            )}
          </div>

          {/* ── Navigation: one flat Menu per section ── */}
          <div className="admin-menu-container">
            {SIDEBAR_SECTIONS.map(section => (
              <div key={section.sectionKey}>
                {/* Section label — hidden when collapsed */}
                {!collapsed && (
                  <div className="admin-sidebar-section-label">{t(section.sectionKey)}</div>
                )}

                <Menu
                  mode="inline"
                  selectedKeys={[activeTab]}
                  theme="dark"
                  className="admin-menu"
                  inlineCollapsed={collapsed}
                  items={section.items.map(item => ({
                    key: item.key,
                    icon: item.icon,
                    label: t(item.labelKey),
                    className: 'admin-menu-item',
                    onClick: () => onTabChange(item.key),
                  }))}
                />
              </div>
            ))}
          </div>

          {/* ── User profile ── */}
          {!collapsed ? (
            <div className="admin-user-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <Avatar
                  size={34}
                  style={{ background: 'var(--admin-primary)', fontWeight: 700, flexShrink: 0 }}
                >
                  {user?.firstName?.[0]?.toUpperCase() || 'A'}
                </Avatar>
                <div style={{ minWidth: 0 }}>
                  <div className="admin-user-card-text-primary">
                    {user ? `${user.firstName} ${user.lastName}` : t('admin.admin_user')}
                  </div>
                  <div className="admin-user-card-text-secondary">
                    {user?.email || 'admin@autoplace.com'}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: 'var(--admin-primary)',
                      background: 'rgba(254, 90, 29, 0.12)',
                      padding: '1px 7px',
                      borderRadius: 4,
                      display: 'inline-block',
                      marginTop: 3,
                      fontWeight: 700,
                      letterSpacing: '0.4px',
                    }}
                  >
                    {user?.role || 'ADMIN'}
                  </div>
                </div>
              </div>

              <Button
                icon={<LogoutOutlined />}
                onClick={logout}
                block
                style={{
                  borderRadius: 8,
                  background: 'rgba(255,255,255,0.05)',
                  borderColor: 'rgba(255,255,255,0.08)',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: 13,
                  fontWeight: 500,
                  height: 34,
                }}
              >
                {t('admin.logout')}
              </Button>
            </div>
          ) : (
            <div style={{ marginTop: 'auto', textAlign: 'center', padding: '0 0 14px', flexShrink: 0 }}>
              <Tooltip
                title={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`}
                placement="right"
              >
                <Avatar
                  size={36}
                  style={{ background: 'var(--admin-primary)', fontWeight: 700 }}
                >
                  {user?.firstName?.[0]?.toUpperCase() || 'A'}
                </Avatar>
              </Tooltip>
            </div>
          )}
        </div>
      </Sider>

      {/* ── Main area ── */}
      <Layout
        style={{ height: '100vh', overflowY: 'auto', background: 'var(--admin-bg)' }}
        className="admin-main-scroll"
      >
        <AdminHeader
          title={meta.label}
          description={meta.description}
          onMenuClick={() => setCollapsed(prev => !prev)}
        />

        <Content
          style={{ padding: '22px 28px 32px', background: 'transparent', minHeight: 'calc(100vh - 64px)' }}
          className="admin-content-area"
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
